import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';
import type { SessionUser } from '@/types/domain';

export const authKeys = {
  all: ['auth'] as const,
  session: () => [...authKeys.all, 'session'] as const,
};

interface SessionResponse {
  user: SessionUser;
}

/**
 * The current session.
 *
 * `retry: false` matters here: a 401 is the normal answer for a signed-out
 * visitor, not a failure to retry, and retrying would delay the redirect to
 * the sign-in page by seconds.
 */
export function useSession(): UseQueryResult<SessionUser | null> {
  return useQuery({
    queryKey: authKeys.session(),
    queryFn: async () => {
      try {
        const { user } = await apiClient.get<SessionResponse>('/auth/me');
        return user;
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: { identifier: string; password: string }) =>
      apiClient.post<SessionResponse>('/auth/login', credentials),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(authKeys.session(), user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.post<{ message: string }>('/auth/logout'),
    onSettled: () => {
      /* Clear everything, not just the session: cached tenant and user lists
         belong to the account that just signed out. */
      queryClient.clear();
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      currentPassword: string;
      newPassword: string;
      confirmPassword: string;
    }) => apiClient.post<{ message: string }>('/auth/change-password', input),
    onSuccess: () => {
      /* The server clears the session cookies, so the client must forget the
         cached session too or the app would look signed in. */
      queryClient.clear();
    },
  });
}
